/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { BlogCategoryFragment, CosmosEnumButtonVariants } from "@cosmos/hygraph-schema";

import { CosmosTagProps } from "@components/elements/atoms";
import { BlogCardProps, ContentLargeImageSectionProps } from "@components/elements/ref-sections";
import { isAbsolute, pathnameHasLocale } from "@cosmos/common-utils";

import { FeedItem } from "./feed-card.types";

const getBlogCategory = (item: FeedItem, blogCategories: BlogCategoryFragment[]): BlogCategoryFragment | undefined => {
	const itemCategoryId = item.categories ? item.categories[0] : "";

	if (!itemCategoryId) return undefined;

	const blogCategory = blogCategories.find((blogCat) => blogCat.categoryId === itemCategoryId);
	return blogCategory;
};

const getCategoryLabel = (item: FeedItem, blogCategories: BlogCategoryFragment[]): string => {
	const blogCategory = getBlogCategory(item, blogCategories);
	return blogCategory?.label || "";
};

const isFeedUrlAbs = (item: FeedItem): boolean => {
	const href = item.url || "";
	const isAbs = isAbsolute(href);
	if (isAbs) {
		return true;
	}
	return false;
};

export const getFeedCardHref = (locale: string, locales: string[], item: FeedItem, currentBrand?: string): string => {
	if (!item.url) return "";

	if (isFeedUrlAbs(item)) {
		return item.url;
	}

	let relativeHref = item.url;
	//ensure url has a leading slash
	if (item.url.charAt(0) !== "/") {
		relativeHref = `/${relativeHref}`;
	}

	if (!pathnameHasLocale(locales, relativeHref)) {
		relativeHref = `/${locale}${relativeHref}`;
	}

	if (currentBrand && item.brand !== currentBrand && item.originUrl) {
		//make href absolute
		return `${item.originUrl}${relativeHref}`;
	}
	return relativeHref;
};

const getBadgeHref = (
	locale: string,
	item: FeedItem,
	blogCategories: BlogCategoryFragment[],
	rootSlug: string,
): string => {
	const blogCategory = getBlogCategory(item, blogCategories);
	if (!blogCategory) return "";
	if (!blogCategory.tag?.tagId) return "";

	return `/${locale}${rootSlug}?tags=${blogCategory.tag?.tagId}`;
};

export const getContentLargeImageFragment = (
	locale: string,
	locales: string[],
	item: FeedItem,
	blogCategories: BlogCategoryFragment[],
	pinnedBadge: CosmosTagProps,
	readMoreLabel: string,
	rootSlug: string,
	currentBrand?: string,
): ContentLargeImageSectionProps => {
	const badgeColor = getBlogCategory(item, blogCategories)?.badgeColor || "";

	return {
		__typename: "CosmosSectionContentLargeImage",
		id: "",
		badges: [
			{
				label: item.sticky ? pinnedBadge.label : undefined,
				icon: item.sticky ? pinnedBadge.icon : undefined,
			},
			{
				label: getCategoryLabel(item, blogCategories),
				href: getBadgeHref(locale, item, blogCategories, rootSlug),
				backgroundColor: badgeColor,
				color: badgeColor ? "black" : "",
			},
		],
		date: item.activeDate,
		title: {
			text: item.title || "",
			html: item.title || "",
		},
		desc: item.description || "",
		asset: {
			id: "",
			url: item?.image?.url || "",
			//empty string to get replaced by hygraphImageLoader
			optUrl: "",
			width: 1200,
			altText: item?.image?.text,
		},
		blurredImage: {
			id: "",
			url: item?.image?.url || "",
			//empty string to get replaced by hygraphImageLoader
			optUrl: "",
			width: 100,
		},
		buttons: [
			{
				label: readMoreLabel || "Read more",
				href: getFeedCardHref(locale, locales, item, currentBrand),
				variant: CosmosEnumButtonVariants.Primary,
			},
		],
	};
};

export const getBlogCardFragment = (
	locale: string,
	locales: string[],
	item: FeedItem,
	blogCategories: BlogCategoryFragment[],
	pinnedBadge: CosmosTagProps,
	rootSlug: string,
	currentBrand?: string,
): BlogCardProps => {
	const badgeColor = getBlogCategory(item, blogCategories)?.badgeColor || "";

	return {
		badges: [
			{
				label: item.sticky ? pinnedBadge.label : undefined,
				icon: item.sticky ? pinnedBadge.icon : undefined,
			},
			{
				label: getCategoryLabel(item, blogCategories),
				href: getBadgeHref(locale, item, blogCategories, rootSlug),
				backgroundColor: badgeColor,
				color: badgeColor ? "black" : "",
			},
		],
		date: item.activeDate,
		title: {
			text: "",
			html: item.title || "",
		},
		desc: item.description || "",
		href: getFeedCardHref(locale, locales, item, currentBrand),
		asset: {
			id: "",
			url: item?.image?.url || "",
			//empty string to get replaced by hygraphImageLoader
			optUrl: "",
			width: 900,
			altText: item?.image?.text,
		},
		likeCount: item.likeCount,
		replyCount: item.replyCount,
		author: item.author,
		buttons: [],
	};
};
